import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import campaignsService from '../../services/campaignsService';

// Async thunks for campaign operations
export const fetchCampaigns = createAsyncThunk(
  'campaigns/fetchCampaigns',
  async (params, { rejectWithValue }) => {
    try {
      const response = await campaignsService.getCampaigns(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch campaigns');
    }
  }
);

export const fetchCampaignsByStatus = createAsyncThunk(
  'campaigns/fetchCampaignsByStatus',
  async ({ status, params = {} }, { rejectWithValue }) => {
    try {
      const response = await campaignsService.getCampaignsByStatus(status, params);
      return { status, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || `Failed to fetch ${status} campaigns`);
    }
  }
);

export const fetchCampaignById = createAsyncThunk(
  'campaigns/fetchCampaignById',
  async (campaignId, { rejectWithValue }) => {
    try {
      const body = await campaignsService.getCampaignById(campaignId);
      if (body && typeof body === 'object' && body.status === true && body.data != null) {
        return body.data;
      }
      return body?.data ?? body;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch campaign');
    }
  }
);

export const activateCampaign = createAsyncThunk(
  'campaigns/activateCampaign',
  async (arg, { rejectWithValue }) => {
    try {
      let campaignId;
      let data;
      if (arg != null && typeof arg === 'object' && !Array.isArray(arg) && 'campaignId' in arg) {
        campaignId = arg.campaignId;
        data = arg.data;
      } else {
        campaignId = arg;
        data = undefined;
      }
      const response = await campaignsService.activateCampaign(campaignId, data);
      return { campaignId, data: response };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to activate campaign');
    }
  }
);

export const updateCampaign = createAsyncThunk(
  'campaigns/updateCampaign',
  async ({ campaignId, updateData }, { rejectWithValue }) => {
    try {
      const response = await campaignsService.updateCampaign(campaignId, updateData);
      return { campaignId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update campaign');
    }
  }
);

export const searchCampaigns = createAsyncThunk(
  'campaigns/searchCampaigns',
  async ({ query, params = {} }, { rejectWithValue }) => {
    try {
      const response = await campaignsService.searchCampaigns(query, params);
      return { query, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to search campaigns');
    }
  }
);

// Initial state
const initialState = {
  // Campaign data organized by status for different tabs
  campaigns: {
    active: { data: [], loading: false, error: null, lastFetched: null },
    upcoming: { data: [], loading: false, error: null, lastFetched: null },
    ended: { data: [], loading: false, error: null, lastFetched: null },
    drafts: { data: [], loading: false, error: null, lastFetched: null },
    all: { data: [], loading: false, error: null, lastFetched: null },
  },
  
  // Individual campaign data
  currentCampaign: null,
  currentCampaignLoading: false,
  currentCampaignError: null,
  
  // Search results
  searchResults: { data: [], loading: false, error: null, query: '' },
  
  // Global state
  loading: false,
  error: null,
  
  // Pagination
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  }
};

// Campaigns slice
const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.currentCampaignError = null;
    },
    
    clearSearchResults: (state) => {
      state.searchResults = { data: [], loading: false, error: null, query: '' };
    },
    
    setCurrentCampaign: (state, action) => {
      state.currentCampaign = action.payload;
      state.currentCampaignLoading = false;
      state.currentCampaignError = null;
    },
    
    clearCurrentCampaign: (state) => {
      state.currentCampaign = null;
      state.currentCampaignLoading = false;
      state.currentCampaignError = null;
    },
    
    updateCampaignInList: (state, action) => {
      const { campaignId, updatedCampaign } = action.payload;
      
      // Update in all relevant lists
      Object.keys(state.campaigns).forEach(status => {
        const campaignIndex = state.campaigns[status].data.findIndex(c => c.id === campaignId);
        if (campaignIndex !== -1) {
          state.campaigns[status].data[campaignIndex] = { ...state.campaigns[status].data[campaignIndex], ...updatedCampaign };
        }
      });
      
      // Update current campaign if it matches
      if (state.currentCampaign && state.currentCampaign.id === campaignId) {
        state.currentCampaign = { ...state.currentCampaign, ...updatedCampaign };
      }
    },
    
    removeCampaignFromList: (state, action) => {
      const campaignId = action.payload;
      
      // Remove from all lists
      Object.keys(state.campaigns).forEach(status => {
        state.campaigns[status].data = state.campaigns[status].data.filter(c => c.id !== campaignId);
      });
      
      // Clear current campaign if it matches
      if (state.currentCampaign && state.currentCampaign.id === campaignId) {
        state.currentCampaign = null;
      }
    }
  },
  
  extraReducers: (builder) => {
    // Fetch campaigns by status
    builder.addCase(fetchCampaignsByStatus.pending, (state, action) => {
      const { status } = action.meta.arg;
      state.campaigns[status].loading = true;
      state.campaigns[status].error = null;
    });
    
    builder.addCase(fetchCampaignsByStatus.fulfilled, (state, action) => {
      const { status, data } = action.payload;
      console.log('🔍 DEBUG: fetchCampaignsByStatus.fulfilled for status:', status, 'data:', data);
      state.campaigns[status].loading = false;
      state.campaigns[status].data = data || [];
      state.campaigns[status].lastFetched = new Date().toISOString();
    });
    
    builder.addCase(fetchCampaignsByStatus.rejected, (state, action) => {
      const { status } = action.meta.arg;
      state.campaigns[status].loading = false;
      state.campaigns[status].error = action.payload || 'Failed to fetch campaigns';
    });
    
    // Fetch single campaign
    builder.addCase(fetchCampaignById.pending, (state) => {
      state.currentCampaignLoading = true;
      state.currentCampaignError = null;
    });
    
    builder.addCase(fetchCampaignById.fulfilled, (state, action) => {
      state.currentCampaignLoading = false;
      state.currentCampaign = action.payload;
    });
    
    builder.addCase(fetchCampaignById.rejected, (state, action) => {
      state.currentCampaignLoading = false;
      state.currentCampaignError = action.payload || 'Failed to fetch campaign';
    });
    
    // Activate campaign
    builder.addCase(activateCampaign.fulfilled, (state, action) => {
      const { campaignId } = action.payload;
      
      // Move campaign from drafts to active
      const draftIndex = state.campaigns.drafts.data.findIndex(c => c.id === campaignId);
      if (draftIndex !== -1) {
        const campaign = state.campaigns.drafts.data.splice(draftIndex, 1)[0];
        state.campaigns.active.data.unshift({ ...campaign, status: 'active' });
      }
      
      // Update current campaign if it matches
      if (state.currentCampaign && state.currentCampaign.id === campaignId) {
        state.currentCampaign.status = 'active';
      }
    });
    
    // Update campaign
    builder.addCase(updateCampaign.fulfilled, (state, action) => {
      const { campaignId, data: updatedCampaign } = action.payload;
      
      // Update in all lists
      Object.keys(state.campaigns).forEach(status => {
        const campaignIndex = state.campaigns[status].data.findIndex(c => c.id === campaignId);
        if (campaignIndex !== -1) {
          state.campaigns[status].data[campaignIndex] = { 
            ...state.campaigns[status].data[campaignIndex], 
            ...updatedCampaign 
          };
        }
      });
      
      // Update current campaign
      if (state.currentCampaign && state.currentCampaign.id === campaignId) {
        state.currentCampaign = { ...state.currentCampaign, ...updatedCampaign };
      }
    });
    
    // Search campaigns
    builder.addCase(searchCampaigns.pending, (state) => {
      state.searchResults.loading = true;
      state.searchResults.error = null;
    });
    
    builder.addCase(searchCampaigns.fulfilled, (state, action) => {
      const { query, data } = action.payload;
      state.searchResults.loading = false;
      state.searchResults.data = data || [];
      state.searchResults.query = query;
    });
    
    builder.addCase(searchCampaigns.rejected, (state, action) => {
      state.searchResults.loading = false;
      state.searchResults.error = action.payload || 'Failed to search campaigns';
    });
  }
});

export const { 
  clearError, 
  clearSearchResults, 
  setCurrentCampaign, 
  clearCurrentCampaign,
  updateCampaignInList,
  removeCampaignFromList
} = campaignsSlice.actions;

export default campaignsSlice.reducer;

// Selectors - Note: campaigns reducer is nested under state.loyalty.campaigns
export const selectCampaignsByStatus = (state, status) => {
  const loyaltyState = state.loyalty || state;
  return loyaltyState.campaigns?.[status] || { data: [], loading: false, error: null };
};

export const selectCampaignsLoading = (state, status) => {
  const loyaltyState = state.loyalty || state;
  return loyaltyState.campaigns?.[status]?.loading || false;
};

export const selectCampaignsError = (state, status) => {
  const loyaltyState = state.loyalty || state;
  return loyaltyState.campaigns?.[status]?.error || null;
};

export const selectCurrentCampaign = (state) => {
  const loyaltyState = state.loyalty || state;
  return loyaltyState.campaigns?.currentCampaign || null;
};

export const selectCurrentCampaignLoading = (state) => {
  const loyaltyState = state.loyalty || state;
  return loyaltyState.campaigns?.currentCampaignLoading || false;
};

export const selectCurrentCampaignError = (state) => {
  const loyaltyState = state.loyalty || state;
  return loyaltyState.campaigns?.currentCampaignError || null;
};

export const selectSearchResults = (state) => {
  const loyaltyState = state.loyalty || state;
  return loyaltyState.campaigns?.searchResults || { data: [], loading: false, error: null, query: '' };
};

export const selectAllCampaignsData = (state) => {
  const loyaltyState = state.loyalty || state;
  const campaigns = loyaltyState.campaigns || {};
  return Object.values(campaigns).flatMap(status => status.data || []);
};